import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Minus, Phone, ArrowRight, HelpCircle } from "lucide-react";
import { FAQS_FULL, COMPANY, ASSETS } from "../mock";
import { SectionHeading } from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

export default function Faqs() {
  const [open, setOpen] = useState(0);
  const navigate = useNavigate();
  useScrollReveal();
  useSEO({
    title: "Frequently Asked Questions | Miller & Miller Services LLC",
    description: "Answers to common questions about pool cages, patio screen enclosures, rescreening, permits, hurricane engineering, financing & service areas in Brevard County, FL.",
    keywords: ["pool cage FAQ", "screen enclosure questions", "pool enclosure permits Brevard County"],
    path: "/faqs",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS_FULL.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.blocks.map((b) => (b.ul ? b.ul.join(". ") : b.p)).join(" ") },
      })),
    },
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-20 bg-brand-navy text-white overflow-hidden">
        <img src={ASSETS.pool2} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/92 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">FAQs</span>
          </nav>
          <div className="flex items-center gap-3 mb-3">
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-gold text-brand-navy"><HelpCircle className="h-7 w-7" /></span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold">Frequently Asked Questions</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">Have questions? We have answers. Learn more about our custom screen structures, process, and service across the Space Coast.</p>
        </div>
      </section>

      {/* FAQ list */}
      <section className="section-y bg-white">
        <div className="container-x max-w-4xl">
          <div className="space-y-3">
            {FAQS_FULL.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className="reveal">
                  <div className={`rounded-xl ring-1 transition-colors ${isOpen ? "bg-white ring-brand-sky/40 shadow-card" : "bg-white ring-brand-mist"}`}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-4">
                    <span className="font-heading font-bold text-brand-navy text-base md:text-lg">{f.q}</span>
                    <span className={`shrink-0 grid place-items-center h-8 w-8 rounded-full transition-colors ${isOpen ? "bg-brand-ocean text-white" : "bg-brand-mist text-brand-ocean"}`}>
                      {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </span>
                  </button>
                  <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                    <div className="overflow-hidden">
                      <div className="px-5 md:px-6 pb-5 space-y-3 text-brand-slate leading-relaxed">
                        {f.blocks.map((b, j) =>
                          b.ul ? (
                            <ul key={j} className="space-y-2">
                              {b.ul.map((li, k) => (
                                <li key={k} className="flex items-start gap-2.5">
                                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-sky shrink-0" />
                                  <span>{li}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p key={j}>{b.p}</p>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-brand-navy text-white p-8 text-center reveal">
            <h3 className="text-xl md:text-2xl font-heading font-extrabold">Still have questions?</h3>
            <p className="text-white/70 mt-2">Our family-owned team is happy to help. Call us or request a free estimate.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate("/estimate")} className="h-12 px-8 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold">
                Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-white/10 hover:bg-white/20 text-white font-heading font-bold ring-1 ring-white/25 transition-colors">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
